import React from 'react';
import AnalyticsSettingsLink from '../../components/AnalyticsSettingsLink';

export default function PrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto pt-24 p-6 space-y-6 text-gray-800">
      <h1 className="text-3xl font-bold">Privacy Policy</h1>
      <p className="text-xl">LettuceTalk</p>
      <p className="text-sm text-gray-500">Last Updated: May 28, 2025</p>
      <p className="text-sm text-gray-600">
        You can change your analytics preference at any time: <AnalyticsSettingsLink className="text-green-700" />.
      </p>

      {/* 1. Definitions */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">1. Definitions</h2>
        <p>
          <strong>“Services”</strong> means LettuceTalk’s website, applications, AI Card Generator, and physical card decks.<br />
          <strong>“We”</strong>, <strong>“Us”</strong> and <strong>“Impact Moonshots”</strong> refer to Creashell LLC (dba Impact Moonshots).<br />
          <strong>“You”</strong> refers to any user of the Services.
        </p>
      </section>

      {/* 2. Introduction */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">2. Introduction</h2>
        <p>
          This Privacy Policy (“Policy”) explains how we collect, use, disclose, and protect personal information in connection with the Services. By using the Services, you agree to this Policy and our <a href="/terms" className="underline">Terms of Service</a>.
        </p>
      </section>

      {/* 3. Information We Collect */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">3. Information We Collect</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Usage Data:</strong> Device info, IP address, pages visited, analytics data (Mixpanel, Google Analytics).</li>
          <li><strong>User Input:</strong> Text, preferences, metadata sent to LLM providers (e.g., ChatGPT).</li>
          <li><strong>Order Data:</strong> Name, shipping address, and purchase details. Payment processors handle card data; we do not store full card numbers.</li>
        </ul>
      </section>

      {/* 4. Use of Information */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">4. Use of Information</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Performance of Contract:</strong> To fulfill orders and provide the Services you request.</li>
          <li><strong>Legitimate Interests:</strong> To improve, secure, and analyze usage of our Services.</li>
          <li><strong>Legal Obligation:</strong> To comply with laws and enforce our Terms of Service.</li>
          <li><strong>Consent:</strong> For non-essential cookies and marketing communications where opted-in.</li>
        </ul>
      </section>

      {/* 5. Cookies & Tracking */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">5. Cookies & Tracking</h2>
        <p>
          We and our providers use cookies and similar technologies to recognize you, remember preferences, and analyze site usage. You’ll see a cookie-consent banner on first visit; you can disable non-essential cookies there or in your browser settings. Disabling cookies may affect site functionality. See our <a href="/cookie-policy" className="underline">Cookie Policy</a> for details.
        </p>
      </section>

      {/* 6. Disclosure & Business Transfers */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">6. Disclosure & Business Transfers</h2>
        <p>We do not sell personal data. We may share data with:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Service Providers:</strong> Under confidentiality obligations (payment processors, shipping carriers, analytics, LLM vendors).</li>
          <li><strong>Legal Requirements:</strong> When compelled by law or court order.</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets. Personal data will remain subject to confidentiality commitments.</li>
        </ul>
      </section>

      {/* 7. Data Security & Liability */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">7. Data Security & Liability</h2>
        <p>
          We implement commercially reasonable measures to protect personal data, but cannot guarantee absolute security. Creashell LLC disclaims liability for unauthorized access or breaches.
        </p>
      </section>

      {/* 8. Data Retention */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">8. Data Retention</h2>
        <p>
          We retain personal data only as necessary to fulfill the purposes set forth or to comply with law. Specific retention periods vary by data type.
        </p>
      </section>

      {/* 9. Your Privacy Rights */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">9. Your Privacy Rights</h2>
        <p>
          Depending on your jurisdiction, you may have the right to access, correct, delete, or export your data and to withdraw consent. To exercise your rights, email care@imoonshots.com.
        </p>
      </section>

      {/* 10. GDPR (EU) */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">10. GDPR (EU) Provisions</h2>
        <p>If you are an EU resident, you have the following additional rights under the GDPR:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Right to access and obtain a copy of your personal data.</li>
          <li>Right to rectify inaccurate or incomplete data.</li>
          <li>Right to erasure (“right to be forgotten”).</li>
          <li>Right to restrict or object to processing.</li>
          <li>Right to data portability.</li>
          <li>Right to withdraw consent at any time without affecting prior processing.</li>
        </ul>
        <p>
          To exercise these rights or lodge a complaint with an EU supervisory authority, contact care@imoonshots.com.
        </p>
      </section>

      {/* 11. California Privacy Rights (CCPA) */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">11. California Privacy Rights</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Right to Know:</strong> California residents may request disclosure of the categories and specific pieces of personal information we collect, use, and share.</li>
          <li><strong>Right to Delete:</strong> You may request deletion of personal information we have collected, subject to certain exceptions.</li>
          <li><strong>Right to Opt-Out of Sale:</strong> We do not “sell” personal information as defined by the CCPA. If this changes, California residents will have a “Do Not Sell My Personal Information” option via our website or by emailing care@imoonshots.com.</li>
          <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
        </ul>
      </section>

      {/* 12. Children’s Privacy */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">12. Children’s Privacy</h2>
        <p>
          Our Services are not directed at children under 13. We will promptly delete any such data upon request.
        </p>
      </section>

      {/* 13. International Transfers */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">13. International Transfers</h2>
        <p>
          Personal data may be transferred to the U.S. or other jurisdictions. We use Standard Contractual Clauses or equivalent safeguards.
        </p>
      </section>

      {/* 14. Changes to This Policy */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">14. Changes to This Policy</h2>
        <p>
          We may update this Policy by posting a revised version with a new “Last Updated” date. Continued use constitutes acceptance.
        </p>
      </section>

      {/* 15. Governing Law & Contact */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">15. Governing Law & Contact</h2>
        <p>
          Governed by California law. For inquiries or to exercise your rights, email care@imoonshots.com.
        </p>
      </section>

      <p className="text-center text-gray-500">
        © 2025 Impact Moonshots. All rights reserved.
      </p>
    </div>
  );
}
